<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useQuery } from '@tanstack/vue-query';
import { routePlansApi, type RoutePlan } from '@/api/route-plans';
import { caregiversApi, type Caregiver } from '@/api/caregivers';
import { useTableFilter } from '@/composables/useTableFilter';
import ResponsiveDialog from '@/components/responsive-dialog.vue';
import TimeInput from '@/components/time-input.vue';

// "+ Agregar profesional": adds a caregiver of the plan's career to the pool with an
// availability window. Their column appears on the calendar right away (with the default
// lunch), ready to receive visits by drag or click.
interface Props {
  modelValue: boolean;
  routePlan: RoutePlan;
}

const props = defineProps<Props>();
const emit = defineEmits<{
  'update:modelValue': [value: boolean];
  'saved': [warnings: string[]];
}>();

const { t } = useI18n();
const HM_LENGTH = 5;
const titleFilter = useTableFilter(['title']);

const caregiverId = ref<number | null>(null);
const availableFrom = ref('');
const availableTo = ref('');
const busy = ref(false);
const errorText = ref('');

const groupParams = computed(() => (props.routePlan.groupId ?
  `groups_id_in[]=${props.routePlan.groupId}` :
  undefined));

const { data: caregivers } = useQuery({
  queryKey: ['caregivers', groupParams],
  queryFn: () => caregiversApi.index(undefined, groupParams.value),
  enabled: computed(() => props.modelValue),
});

const poolIds = computed(
  () => new Set(props.routePlan.routePlanCaregivers.map(rpc => rpc.caregiverId)),
);

// Same career as the plan, not already in the pool.
const caregiverOptions = computed(() => (caregivers.value ?? [])
  .filter((c: Caregiver) => c.career === props.routePlan.career && !poolIds.value.has(c.id))
  .map((c: Caregiver) => ({ value: c.id, title: `${c.firstName} ${c.lastName}` })));

function hm(value: string): string {
  const start = value.includes('T') ? value.indexOf('T') + 1 : 0;

  return value.slice(start, start + HM_LENGTH);
}

// Prefill with the pool's typical window so the new caregiver matches the plan's day.
function reset() {
  caregiverId.value = null;
  errorText.value = '';
  const reference = props.routePlan.routePlanCaregivers[0];
  availableFrom.value = reference ? hm(reference.availableFrom) : '08:00';
  availableTo.value = reference ? hm(reference.availableTo) : '20:30';
}

watch(() => props.modelValue, (open) => {
  if (open) reset();
}, { immediate: true });

const canSave = computed(() => caregiverId.value !== null &&
  !!availableFrom.value && !!availableTo.value);

function requestError(error: unknown): string {
  const data = (error as { response?: { data?: { errors?: string[] } } })?.response?.data;

  return data?.errors?.length ? data.errors.join(' · ') : t('routePlan.visitForm.saveError');
}

async function onSave() {
  if (!canSave.value || busy.value) return;
  busy.value = true;
  errorText.value = '';

  try {
    await routePlansApi.addCaregiver(props.routePlan.id, caregiverId.value!, {
      availableFrom: availableFrom.value, availableTo: availableTo.value,
    });
    emit('saved', []);
    emit('update:modelValue', false);
  } catch (error) {
    errorText.value = requestError(error);
  } finally {
    busy.value = false;
  }
}
</script>

<template>
  <responsive-dialog
    :model-value="modelValue"
    max-width="560"
    @update:model-value="emit('update:modelValue', $event)"
  >
    <v-card class="pa-2">
      <v-card-title class="d-flex align-center ga-3">
        <span>{{ t('routePlan.addCaregiver.title') }}</span>
        <v-spacer />
        <v-btn
          icon="mdi-close"
          variant="text"
          size="small"
          :aria-label="t('routePlan.cancel')"
          @click="emit('update:modelValue', false)"
        />
      </v-card-title>

      <v-card-text>
        <v-alert
          type="info"
          variant="tonal"
          density="compact"
          class="mb-6"
        >
          {{ t('routePlan.addCaregiver.subtitle') }}
        </v-alert>

        <v-autocomplete
          v-model="caregiverId"
          :items="caregiverOptions"
          :custom-filter="titleFilter"
          :label="t('routePlan.visitForm.caregiver')"
          :no-data-text="t('routePlan.addCaregiver.empty')"
          variant="outlined"
          density="comfortable"
          hide-details="auto"
        />

        <div class="text-subtitle-2 font-weight-medium mt-6">
          {{ t('routePlan.addCaregiver.availability') }}
        </div>
        <p class="text-body-2 text-medium-emphasis mb-3">
          {{ t('routePlan.addCaregiver.availabilityHint') }}
        </p>
        <v-row class="mt-0">
          <v-col
            cols="12"
            sm="6"
          >
            <time-input
              v-model="availableFrom"
              density="comfortable"
              :label="t('routePlan.addCaregiver.from')"
            />
          </v-col>
          <v-col
            cols="12"
            sm="6"
          >
            <time-input
              v-model="availableTo"
              density="comfortable"
              :label="t('routePlan.addCaregiver.to')"
            />
          </v-col>
        </v-row>

        <v-alert
          v-if="errorText"
          type="error"
          variant="tonal"
          density="compact"
          class="mt-4"
        >
          {{ errorText }}
        </v-alert>
      </v-card-text>

      <v-card-actions class="px-4 pb-3 pt-0">
        <v-spacer />
        <v-btn
          color="primary"
          variant="flat"
          :loading="busy"
          :disabled="!canSave"
          @click="onSave"
        >
          {{ t('routePlan.addCaregiver.save') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </responsive-dialog>
</template>
